import { useState } from 'react'
import { useLocale } from '../../App'

interface ShareModalProps {
  reason: string
  authToken?: string | null
  onClose: () => void
  onShare: () => void
}

export function ShareModal({ reason, authToken, onClose, onShare }: ShareModalProps) {
  const { t } = useLocale()
  const [sharing, setSharing] = useState(false)
  const [shared, setShared] = useState(false)
  const [rewardAmount, setRewardAmount] = useState(0)
  const [newBalance, setNewBalance] = useState(0)
  const [shareErr, setShareErr] = useState<string | null>(null)

  const handleShare = async () => {
    setSharing(true)
    setShareErr(null)

    if (authToken) {
      try {
        const res = await fetch('/api/share/verify', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
          body: JSON.stringify({ shareId: 'revive_auto', deviceFingerprint: '' }),
        })
        const data = await res.json()
        if (!res.ok) {
          setShareErr(data.message || 'Share failed')
          setSharing(false)
          return
        }
        if (data.rewardGranted) setRewardAmount(data.rewardGranted)
        if (data.newBalance) setNewBalance(data.newBalance)
      } catch {
        setShareErr('Network error')
        setSharing(false)
        return
      }
    }

    const tg = window.Telegram?.WebApp
    const url = 'https://poker.usdt.bz/s/play'
    const text = t('app.share_topbar')
    try {
      if (tg?.openTelegramLink) tg.openTelegramLink('https://t.me/share/url?url=' + encodeURIComponent(url) + '&text=' + encodeURIComponent(text))
      else if (tg?.shareUrl) tg.shareUrl(url)
      else window.open('https://t.me/share/url?url=' + encodeURIComponent(url) + '&text=' + encodeURIComponent(text), '_blank')
    } catch {}

    setSharing(false)
    setShared(true)
  }

  const isRevive = reason === 'revive'

  if (shared) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
        <div className="w-full max-w-sm rounded-2xl overflow-hidden bg-slate-900 border border-slate-700/50 shadow-2xl">
          <div className="px-6 pt-8 pb-4 text-center"
            style={{ background: 'linear-gradient(180deg, rgba(16,185,129,0.15) 0%, transparent 100%)' }}
          >
            <div className="mb-3 text-6xl">🎉</div>
            <h2 className="text-xl font-extrabold text-white">
              {t('app.share_revive_success_title')}
            </h2>
            <p className="mt-3 text-lg font-bold text-emerald-400">
              {rewardAmount > 0 && `${t('app.share_revive_got')} ${rewardAmount} ${t('app.chips')}！`}
            </p>
            <p className="mt-1 text-sm text-slate-400">
              {t('app.balance')}：<span className="font-bold text-yellow-400">{newBalance.toLocaleString()}</span> {t('app.chips')}
            </p>
          </div>
          <div className="flex flex-col gap-2.5 px-6 pb-6">
            <button
              onClick={onShare}
              className="rounded-xl px-6 py-3 text-base font-bold text-white transition-all active:scale-95 shadow-lg"
              style={{
                background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
                boxShadow: '0 4px 14px rgba(59, 130, 246, 0.3)',
              }}
            >
              🃏 {t('app.start')}
            </button>
            <button
              onClick={onClose}
              className="rounded-xl px-6 py-2.5 text-sm font-medium text-slate-500 hover:text-slate-300 transition-colors"
            >
              🏠 {t('app.back_home')}
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="w-full max-w-sm rounded-2xl overflow-hidden bg-slate-900 border border-slate-700/50 shadow-2xl">
        <div
          className="px-6 pt-8 pb-4 text-center"
          style={{
            background: isRevive
              ? 'linear-gradient(180deg, rgba(239,68,68,0.15) 0%, transparent 100%)'
              : 'linear-gradient(180deg, rgba(59,130,246,0.15) 0%, transparent 100%)',
          }}
        >
          <div className="mb-3 text-6xl">{isRevive ? '💸' : '🎉'}</div>
          <h2 className="text-xl font-extrabold text-white">
            {isRevive ? t('app.share_revive_title') : t('app.share_earn')}
          </h2>
          <p className="mt-2 text-sm text-slate-400 leading-relaxed">
            {isRevive ? t('app.share_revive_desc') : t('app.share_earn_desc')}
          </p>
          {shareErr && (
            <p className="mt-3 text-sm font-medium text-red-400">{shareErr}</p>
          )}
        </div>

        <div className="flex flex-col gap-2.5 px-6 pb-6">
          <button
            onClick={handleShare}
            disabled={sharing}
            className="rounded-xl px-6 py-3 text-base font-bold text-white transition-all active:scale-95 disabled:opacity-50 shadow-lg"
            style={{
              background: isRevive
                ? 'linear-gradient(135deg, #ef4444, #b91c1c)'
                : 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
              boxShadow: isRevive
                ? '0 4px 14px rgba(239, 68, 68, 0.3)'
                : '0 4px 14px rgba(59, 130, 246, 0.3)',
            }}
          >
            {sharing ? t('app.starting') : isRevive ? t('app.share_revive_btn') : t('app.share_earn_btn')}
          </button>
          <button
            onClick={onClose}
            disabled={sharing}
            className="rounded-xl px-6 py-2.5 text-sm font-medium text-slate-500 hover:text-slate-300 transition-colors disabled:opacity-50"
          >
            {t('app.share_later')}
          </button>
        </div>
      </div>
    </div>
  )
}